<script setup lang="ts">
import { computed, provide, useId } from 'vue'
import FieldHelperIcon from '@/components/FieldHelperIcon.vue'
import { FieldContextKey } from '@/components/base/fieldContext'

const props = defineProps<{
  label: string
  error?: string
  required?: boolean
  helperText?: string
  helperIcon?: string
  hint?: string
  /** Override the generated id, e.g. when a test or a label elsewhere targets it. */
  id?: string
}>()

const uid = useId()

const fieldId = computed(() => props.id ?? `fld-${uid}`)
const invalid = computed(() => !!props.error)
const errorId = computed(() => `${fieldId.value}-error`)
const helperId = computed(() => `${fieldId.value}-helper`)

const describedBy = computed(() => {
  if (props.error) return errorId.value
  if (props.helperText) return helperId.value
  return undefined
})

/**
 * Publish the field's state to whatever control sits in the slot. A BaseInput
 * inside a FormField picks up its id, its invalid styling and its
 * aria-describedby from here, so the call site states the error once instead of
 * repeating a getError() check on both the wrapper and the control.
 */
provide(FieldContextKey, { id: fieldId, invalid, describedBy })
</script>

<template>
  <div class="space-y-1">
    <div class="flex items-center">
      <label :for="fieldId" class="block text-sm font-medium text-gray-700">
        <slot name="label" :label="label" :required="required">
          {{ label }}
          <span v-if="required" class="text-red-600">*</span>
        </slot>
      </label>
      <div v-if="helperIcon" class="ml-2">
        <FieldHelperIcon :helper-text="helperIcon" />
      </div>
      <span v-if="hint || $slots.hint" class="ml-auto pl-3 text-xs text-gray-500">
        <slot name="hint">{{ hint }}</slot>
      </span>
    </div>
    <slot />
    <p v-if="error" :id="errorId" class="text-xs text-red-600 mt-1">
      {{ error }}
    </p>
    <p v-else-if="helperText" :id="helperId" class="text-xs text-gray-500 mt-1">
      {{ helperText }}
    </p>
  </div>
</template>

<style scoped></style>
